import React, { useEffect, useState, useRef } from 'react';
/**
 * TrueFalseSeriesRenderer — True/false toggle for multiple statements.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s66.
 */
import { QuestionRendererProps } from './types';
import { Check, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { hexToRgba, FALLBACK_COLOR } from './rendererUtils';
export function TrueFalseSeriesRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data || {};
  const statements: Array<{
    text: string;
    isTrue: boolean;
  }> = config.statements || [];
  const [answers, setAnswers] = useState<Record<number, boolean>>({});
  const prevValidateRef = useRef(false);
  useEffect(() => {
    setAnswers({});
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) {
      prevValidateRef.current = false;
    }
  }, [shouldValidate]);
  useEffect(() => {
    onReadyChange?.(Object.keys(answers).length === statements.length);
  }, [answers, statements.length, onReadyChange]);
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      const answerArray = statements.map((_, idx) => answers[idx]);
      onAnswer(JSON.stringify(answerArray));
    }
  }, [shouldValidate, answers, statements, onAnswer]);
  const handleSelect = (index: number, value: boolean) => {
    setAnswers(prev => ({
      ...prev,
      [index]: value
    }));
  };
  if (statements.length === 0) {
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-8812">
        <p className="text-red-700 font-bold" data-id="element-8813">⚠️ Geen stellingen gevonden</p>
      </div>;
  }
  return <div className="w-full max-w-2xl mx-auto space-y-4" data-id="element-8814">
      {statements.map((stmt, index) => {
      const isAnswered = answers[index] !== undefined;
      const answer = answers[index];
      return <motion.div key={index} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.1
      }} className={`p-4 sm:p-5 rounded-2xl border-2 transition-all ${isAnswered ? 'bg-white shadow-md' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`} style={isAnswered ? {
        borderColor: hexToRgba(color, 0.3)
      } : undefined} data-id="element-8815">
            <p className="text-slate-800 font-medium text-lg mb-4" data-id="element-8816">
              {stmt.text}
            </p>
            <div className="flex gap-3" data-id="element-8817">
              <button onClick={() => handleSelect(index, true)} className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold transition-all ${answer === true ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 scale-[1.02]' : 'bg-white text-slate-600 border-2 border-slate-200 hover:border-emerald-200 hover:bg-emerald-50'}`} data-id="element-8818">
                <Check className={`w-5 h-5 ${answer === true ? 'text-white' : 'text-emerald-500'}`} data-id="element-8819" />
                Waar
              </button>
              <button onClick={() => handleSelect(index, false)} className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold transition-all ${answer === false ? 'bg-red-500 text-white shadow-lg shadow-red-500/30 scale-[1.02]' : 'bg-white text-slate-600 border-2 border-slate-200 hover:border-red-200 hover:bg-red-50'}`} data-id="element-8820">
                <X className={`w-5 h-5 ${answer === false ? 'text-white' : 'text-red-500'}`} data-id="element-8821" />
                Onwaar
              </button>
            </div>
          </motion.div>;
    })}
    </div>;
}